const TELEGRAM_HOSTS = new Set(['t.me', 'www.t.me', 'telegram.me', 'www.telegram.me']);
const TELEGRAM_USERNAME_PATTERN = /^[A-Za-z0-9_]{5,32}$/;
const TELEGRAM_URL_PATTERN = /(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me)\/[^\s<>"']+/giu;
const SAFE_ROUTE_PARAMETERS = ['fm', 'lang', 'loc', 'ch', 'backend'] as const;
const MAX_BACKEND_DETAILS_LENGTH = 500;
const MAX_ERROR_MESSAGE_LENGTH = 750;
const MAX_STACK_LENGTH = 1_200;
const MAX_REPORT_LENGTH = 3_500;

export type TelegramSupportReportLabels = {
  intro: string;
  consoleError: string;
  error: string;
  httpStatus: string;
  backendDetails: string;
  stackTrace: string;
  diagnosticContext: string;
  timestampUtc: string;
  foodMaker: string;
  location: string;
  route: string;
  frontendOrigin: string;
  backendOrigin: string;
  applicationVersion: string;
  pageVersion: string;
  buildTime: string;
};

export type TelegramSupportReportContext = {
  error: unknown;
  timestamp: Date;
  foodMakerCode: string;
  location: string;
  currentUrl: string;
  frontendOrigin: string;
  backendOrigin: string;
  applicationVersion: string;
  pageVersion: string;
  buildTime: string;
  labels: TelegramSupportReportLabels;
};

type SupportErrorDetails = {
  name: string;
  message: string;
  status: number | null;
  backendDetails: string | null;
  stack: string | null;
};

/**
 * OrderSubmissionError
 *
 * Retains the safe subset of a failed order response that customer support
 * needs. The request payload is deliberately never attached to the error, so
 * personal information and cart contents cannot reach the support report.
 */
export class OrderSubmissionError extends Error {
  readonly status: number;
  readonly backendDetails: string | null;

  constructor(status: number, backendDetails: string | null) {
    super(`Order request failed with status ${status}`);
    this.name = 'OrderSubmissionError';
    this.status = status;
    this.backendDetails = backendDetails;
    Object.setPrototypeOf(this, OrderSubmissionError.prototype);
  }
}

const truncateText = (value: string, maximumLength: number): string => {
  const characters = Array.from(value);
  return characters.length <= maximumLength
    ? value
    : `${characters.slice(0, Math.max(0, maximumLength - 1)).join('')}…`;
};

const normalizeDiagnosticWhitespace = (value: string): string => (
  Array.from(value)
    .map((character) => {
      const characterCode = character.charCodeAt(0);
      const isUnsupportedControlCharacter = characterCode <= 8
        || (characterCode >= 11 && characterCode <= 12)
        || (characterCode >= 14 && characterCode <= 31)
        || characterCode === 127;
      return isUnsupportedControlCharacter ? ' ' : character;
    })
    .join('')
    .replace(/[ \t]+/gu, ' ')
    .trim()
);

/**
 * sanitizeDiagnosticText
 *
 * Redacts common customer identifiers and credentials from caught messages,
 * backend detail strings, and stack traces. It is intentionally applied only
 * to failure text; known-safe diagnostic metadata remains readable.
 */
const sanitizeDiagnosticText = (value: string, maximumLength: number): string => {
  const redacted = value
    .replace(
      /([?&](?:ui|user_id|telegram_user_id|phone|phone_number|address|token|auth|authorization)=)[^&#\s]*/giu,
      '$1[redacted]',
    )
    .replace(
      /(["']?(?:ui|user_id|telegram_user_id|phone|phone_number|address|token|auth|authorization|password|order_data|cart_items)["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^,\s}]+)/giu,
      '$1[redacted]',
    )
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/giu, '[redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/gu, '[redacted]')
    .replace(/\+?\d[\d ()-]{7,}\d/gu, '[redacted]');

  return truncateText(normalizeDiagnosticWhitespace(redacted), maximumLength);
};

const isUnknownRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

/**
 * getSafeBackendErrorDetail
 *
 * Reads only established textual error fields from a backend response. It
 * avoids serializing arbitrary response objects, which could contain order or
 * customer data, and applies the same redaction used by support reports.
 */
export const getSafeBackendErrorDetail = (payload: unknown): string | null => {
  if (!isUnknownRecord(payload)) {
    return null;
  }

  for (const key of ['message', 'detail', 'error'] as const) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return sanitizeDiagnosticText(value, MAX_BACKEND_DETAILS_LENGTH) || null;
    }
  }

  return null;
};

/**
 * normalizeTelegramSupportUrl
 *
 * Accepts only Telegram public-username links and rebuilds them on HTTPS. Any
 * untrusted query or fragment is discarded before the application adds its
 * own prepared support message.
 */
export const normalizeTelegramSupportUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }

  const trimmedValue = value.trim().replace(/[),.;!?]+$/gu, '');
  const candidate = /^https?:\/\//iu.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;

  try {
    const url = new URL(candidate);
    if (!TELEGRAM_HOSTS.has(url.hostname.toLowerCase())) {
      return null;
    }

    const pathSegments = url.pathname.split('/').filter(Boolean);
    if (pathSegments.length !== 1 || !TELEGRAM_USERNAME_PATTERN.test(pathSegments[0])) {
      return null;
    }

    return `https://t.me/${pathSegments[0]}`;
  } catch {
    return null;
  }
};

/**
 * resolveTelegramSupportUrl
 *
 * Prefers the structured partner support link. Older translations sometimes
 * embed the destination in the receipt message, so the first valid Telegram
 * username link in that message remains a compatibility fallback.
 */
export const resolveTelegramSupportUrl = (
  configuredLink: unknown,
  translatedErrorMessage: string,
): string | null => {
  const configuredUrl = normalizeTelegramSupportUrl(configuredLink);
  if (configuredUrl) {
    return configuredUrl;
  }

  const embeddedCandidates = translatedErrorMessage.match(TELEGRAM_URL_PATTERN) ?? [];
  for (const candidate of embeddedCandidates) {
    const normalizedCandidate = normalizeTelegramSupportUrl(candidate);
    if (normalizedCandidate) {
      return normalizedCandidate;
    }
  }

  return null;
};

/**
 * stripTelegramSupportUrls
 *
 * Removes legacy Telegram URLs after a valid support action has been created,
 * keeping the visible alert concise and avoiding a duplicate raw link.
 */
export const stripTelegramSupportUrls = (message: string): string => (
  message
    .replace(TELEGRAM_URL_PATTERN, '')
    .replace(/\s+([,.;!?])/gu, '$1')
    .replace(/\s{2,}/gu, ' ')
    .trim()
);

/**
 * buildSafeSupportRoute
 *
 * Reconstructs the current route from a strict diagnostic allow-list. User IDs
 * and any unrecognized query parameters are omitted by default.
 */
export const buildSafeSupportRoute = (currentUrl: string): string => {
  try {
    const url = new URL(currentUrl, 'https://ifood.invalid');
    const safeSearch = new URLSearchParams();

    SAFE_ROUTE_PARAMETERS.forEach((parameter) => {
      const value = url.searchParams.get(parameter);
      if (value) {
        safeSearch.set(parameter, value);
      }
    });

    const search = safeSearch.toString();
    return `${url.pathname || '/'}${search ? `?${search}` : ''}`;
  } catch {
    return '/Menu';
  }
};

const getSupportErrorDetails = (error: unknown): SupportErrorDetails => {
  const errorRecord = isUnknownRecord(error) ? error : null;
  const statusValue = errorRecord?.status;
  const status = typeof statusValue === 'number' && Number.isInteger(statusValue) && statusValue >= 100
    ? statusValue
    : null;
  const backendDetailsValue = errorRecord?.backendDetails;
  const backendDetails = typeof backendDetailsValue === 'string' && backendDetailsValue.trim().length > 0
    ? sanitizeDiagnosticText(backendDetailsValue, MAX_BACKEND_DETAILS_LENGTH)
    : null;

  if (error instanceof Error) {
    return {
      name: sanitizeDiagnosticText(error.name || 'Error', 100),
      message: sanitizeDiagnosticText(error.message || 'Unknown order submission error.', MAX_ERROR_MESSAGE_LENGTH),
      status,
      backendDetails,
      stack: error.stack ? sanitizeDiagnosticText(error.stack, MAX_STACK_LENGTH) : null,
    };
  }

  return {
    name: 'Error',
    message: typeof error === 'string'
      ? sanitizeDiagnosticText(error, MAX_ERROR_MESSAGE_LENGTH)
      : 'Unknown order submission error.',
    status,
    backendDetails,
    stack: null,
  };
};

/**
 * buildTelegramSupportReport
 *
 * Formats the failure-specific console entry plus a bounded set of operational
 * metadata. It never receives the order payload, customer profile, or cart and
 * therefore cannot add those values to the Telegram draft.
 */
export const buildTelegramSupportReport = (context: TelegramSupportReportContext): string => {
  const { labels } = context;
  const errorDetails = getSupportErrorDetails(context.error);
  const lines = [
    labels.intro,
    '',
    `${labels.consoleError}:`,
    'Failed to submit order',
    `${labels.error}: ${errorDetails.name}: ${errorDetails.message}`,
  ];

  if (errorDetails.status != null) {
    lines.push(`${labels.httpStatus}: ${errorDetails.status}`);
  }
  if (errorDetails.backendDetails) {
    lines.push(`${labels.backendDetails}: ${errorDetails.backendDetails}`);
  }

  lines.push(
    '',
    `${labels.diagnosticContext}:`,
    `${labels.timestampUtc}: ${context.timestamp.toISOString()}`,
    `${labels.foodMaker}: ${context.foodMakerCode || 'unknown'}`,
    `${labels.location}: ${context.location || 'unknown'}`,
    `${labels.route}: ${buildSafeSupportRoute(context.currentUrl)}`,
    `${labels.frontendOrigin}: ${context.frontendOrigin}`,
    `${labels.backendOrigin}: ${context.backendOrigin}`,
    `${labels.applicationVersion}: ${context.applicationVersion}`,
    `${labels.pageVersion}: ${context.pageVersion}`,
    `${labels.buildTime}: ${context.buildTime}`,
  );

  if (errorDetails.stack) {
    lines.push('', `${labels.stackTrace}:`, errorDetails.stack);
  }

  return truncateText(lines.join('\n'), MAX_REPORT_LENGTH);
};

/**
 * buildTelegramSupportHref
 *
 * Adds the prepared report to a previously validated Telegram username link.
 * Telegram opens this value as a draft; the customer remains responsible for
 * reviewing and sending it.
 */
export const buildTelegramSupportHref = (supportUrl: string, report: string): string | null => {
  const normalizedUrl = normalizeTelegramSupportUrl(supportUrl);
  if (!normalizedUrl) {
    return null;
  }

  const url = new URL(normalizedUrl);
  url.searchParams.set('text', truncateText(report, MAX_REPORT_LENGTH));
  return url.toString();
};
